import {
    FETCH_BOOKING_LOCATION,
    FETCH_BOOKING_LOCATION_SUCCESS,
    FETCH_BOOKING_LOCATION_FAILED,
    STOP_LOCATION_FETCH,
    STORE_ADRESSES
} from "../store/types";
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { query, onValue, set, off, push, limitToLast, ref, remove, onDisconnect, serverTimestamp } from "firebase/database";
import { encodeGeohash, LOCATION_GEOHASH_PRECISION } from '../other/geohash';
import { shouldWriteLocation } from '../other/locationThrottle';

export const saveTracking = (bookingId, location) => {
    const {
        trackingRef
    } = firebase;
    push(trackingRef(bookingId), location);
}

/** Una sola suscripción de tracking a la vez: las pantallas llaman fetchBookingLocations en cada render/snapshot. */
let trackingSubscription = { bookingId: null, unsubscribe: null };

const stopTrackingSubscription = () => {
    if (typeof trackingSubscription.unsubscribe === 'function') {
        try { trackingSubscription.unsubscribe(); } catch (_) { /* noop */ }
    }
    trackingSubscription = { bookingId: null, unsubscribe: null };
};

export const fetchBookingLocations = (bookingId) => (dispatch) => {

    const {
        trackingRef
    } = firebase;

    if (!bookingId) return;
    if (trackingSubscription.bookingId === bookingId && trackingSubscription.unsubscribe) return;
    stopTrackingSubscription();

    dispatch({
        type: FETCH_BOOKING_LOCATION,
        payload: bookingId,
    });
    const unsubscribe = onValue(query(trackingRef(bookingId), limitToLast(1)),(snapshot) => {
        if (snapshot.val()) {
            let data = snapshot.val();
            const locations = Object.keys(data)
                .map((i) => {
                    return data[i]
                });
            if (locations.length == 1) {
                dispatch({
                    type: FETCH_BOOKING_LOCATION_SUCCESS,
                    payload: locations[0]
                });
            }
            else {
                dispatch({
                    type: FETCH_BOOKING_LOCATION_FAILED,
                    payload: store.getState().languagedata.defaultLanguage.location_fetch_error,
                });
            }
        } else {
            dispatch({
                type: FETCH_BOOKING_LOCATION_FAILED,
                payload: store.getState().languagedata.defaultLanguage.location_fetch_error,
            });
        }
    }, () => {
        if (trackingSubscription.bookingId === bookingId) {
            trackingSubscription = { bookingId: null, unsubscribe: null };
        }
    });
    trackingSubscription = { bookingId, unsubscribe };
};

export const stopLocationFetch = (bookingId) => (dispatch) => {

    const {
        trackingRef
    } = firebase;

    dispatch({
        type: STOP_LOCATION_FETCH,
        payload: bookingId,
    });
    if (!bookingId || trackingSubscription.bookingId === bookingId) {
        stopTrackingSubscription();
    }
    off(trackingRef(bookingId));
}

let lastLocationWrite = null;
/** Última posición recibida: al reconectar se reescribe sin esperar a que el GPS (iOS, carro quieto) emita. */
let lastKnownLocation = null;
const emptyPresence = () => ({ uid: null, unsubscribe: null, armed: false, connected: false, lost: false });
let presence = emptyPresence();

const stopPresence = () => {
    if (typeof presence.unsubscribe === 'function') {
        try { presence.unsubscribe(); } catch (_) { /* noop */ }
    }
    presence = emptyPresence();
};

/** Al reconectar, el servidor ya ejecutó el borrado programado: la siguiente posición se escribe enseguida. */
const watchPresence = (uid) => {
    if (presence.uid === uid) return;
    stopPresence();
    presence.uid = uid;
    presence.unsubscribe = onValue(ref(firebase.database, '.info/connected'), (snap) => {
        if (snap.val() === true) {
            const reconnected = presence.lost;
            if (reconnected) {
                lastLocationWrite = null;
                presence.armed = false;
            }
            presence.connected = true;
            presence.lost = false;
            if (reconnected && lastKnownLocation && presence.uid === uid) {
                saveUserLocation(lastKnownLocation);
            }
        } else if (presence.connected) {
            presence.connected = false;
            presence.lost = true;
        }
    });
};

/**
 * Guarda la ubicación del conductor con su geohash: el mapa del pasajero busca conductores
 * cercanos con consultas por prefijo de geohash sobre /locations (índice en las reglas).
 * Escribe como máximo cada 14 s si se movió 50 m, y cada 2 min aunque no se mueva; si la app
 * pierde la conexión, el servidor borra la ubicación.
 */
export const saveUserLocation = (location) => {
    const {
      auth,
      userLocationRef,
    } = firebase;
    if (!auth?.currentUser?.uid || !location || location.lat == null || location.lng == null) {
      return;
    }
    const uid = auth.currentUser.uid;
    lastKnownLocation = location;
    watchPresence(uid);
    const now = Date.now();
    if (!shouldWriteLocation(lastLocationWrite, location, now)) return;
    lastLocationWrite = { at: now, lat: Number(location.lat), lng: Number(location.lng) };

    const locationRef = userLocationRef(uid);
    if (!presence.armed) {
      presence.armed = true;
      onDisconnect(locationRef).remove().catch(() => { presence.armed = false; });
    }
    const geohash = encodeGeohash(Number(location.lat), Number(location.lng), LOCATION_GEOHASH_PRECISION);
    const payload = {
      lat: location.lat,
      lng: location.lng,
      geohash,
      ts: serverTimestamp(),
      ...(location.heading != null ? { heading: location.heading } : {}),
      ...(location.speed != null ? { speed: location.speed } : {}),
    };

    set(locationRef, payload).catch((err) => {
      lastLocationWrite = null;
      console.warn('[saveUserLocation] write failed:', err?.message || err);
    });
}

/** Conductor fuera de línea: deja de aparecer en /locations y no se programa más el borrado. */
export const removeUserLocation = () => {
    const { auth, userLocationRef } = firebase;
    const uid = auth?.currentUser?.uid || presence.uid;
    lastLocationWrite = null;
    lastKnownLocation = null;
    stopPresence();
    if (!uid) return Promise.resolve();
    const locationRef = userLocationRef(uid);
    return onDisconnect(locationRef).cancel()
      .catch(() => {})
      .then(() => remove(locationRef))
      .catch((err) => {
        console.warn('[removeUserLocation] failed:', err?.message || err);
      });
}

/** Solo la app del conductor asignado escribe (reglas). */
export const saveBookingRoute = (bookingId, route) => {
    const { bookingRouteRef } = firebase;
    if (!bookingId || !route) return Promise.resolve();
    return set(bookingRouteRef(bookingId), route);
}

/** onRoute(route | null) en cada cambio; devuelve la función para dejar de escuchar. */
export const subscribeBookingRoute = (bookingId, onRoute) => {
    const { bookingRouteRef } = firebase;
    if (!bookingId) return () => {};
    return onValue(bookingRouteRef(bookingId), (snapshot) => {
        onRoute(snapshot.exists() ? snapshot.val() : null);
    }, () => onRoute(null));
}

/** Recorrido GPS completo de un viaje (archivado o en curso). Lo sirve la Cloud Function trip_tracking. */
export const fetchTripTracking = async (bookingId) => {
    const { auth, config } = firebase;
    const settings = store.getState().settingsdata.settings;
    const host =
      typeof window !== 'undefined' && window.location && settings.CompanyWebsite === window.location.origin
        ? window.location.origin
        : `https://${config.projectId}.web.app`;
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${host}/trip_tracking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ bookingId }),
      });
      const result = await response.json();
      if (result.success) return result;
      return { success: false, error: result.error || 'No se pudo cargar el recorrido.' };
    } catch (error) {
      return { success: false, error: 'No se pudo contactar al servidor. Revisa tu conexión e intenta de nuevo.' };
    }
}

export const storeAddresses = (data) => (dispatch) => {
    dispatch({
        type: STORE_ADRESSES,
        payload: data,
    });
}
